import { useState, useEffect } from "react";

const APP_NAME = "RetroBric";
const DEV_NAME = "AlphaY";
const DEV_EMAIL = "alphay28071994@gmail.com";
const PACKAGE = "com.alphay.retrobric";
const EFFECTIVE_DATE = "May 2026";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Press+Start+2P&family=VT323&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }

:root {
  --bg:       #080808;
  --bg2:      #0E0E0E;
  --surface:  #111111;
  --elevated: #1A1A1A;
  --border:   #2A2A2A;
  --border2:  #3A3A3A;
  --yellow:   #FFDE00;
  --yellow2:  #D4B800;
  --gray:     #888888;
  --white:    #E8E8E8;
  --lcd:      #8BA876;
  --lcd-dark: #1A2E0A;
  --red:      #FF4444;
  --green:    #4CAF50;
  --radius:   4px;
  --px:       'Press Start 2P', monospace;
  --vt:       'VT323', monospace;
}

body {
  background: var(--bg);
  color: var(--white);
  font-family: var(--vt);
  font-size: 18px;
  line-height: 1.7;
  -webkit-font-smoothing: none;
  image-rendering: pixelated;
  overflow-x: hidden;
}

/* Scanline overlay */
body::before {
  content: '';
  position: fixed; inset: 0; z-index: 9999;
  pointer-events: none;
  background: repeating-linear-gradient(
    0deg,
    transparent,
    transparent 2px,
    rgba(0,0,0,0.08) 2px,
    rgba(0,0,0,0.08) 4px
  );
}

/* Pixel dot pattern bg */
.rb-dots {
  position: fixed; inset: 0; z-index: 0; pointer-events: none;
  background-image: radial-gradient(circle, #1E1E1E 1px, transparent 1px);
  background-size: 24px 24px;
  opacity: 0.6;
}

/* Yellow glow top */
.rb-glow {
  position: fixed; top: -120px; left: 50%; transform: translateX(-50%);
  width: 600px; height: 300px; z-index: 0; pointer-events: none;
  background: radial-gradient(ellipse, rgba(255,222,0,0.06) 0%, transparent 70%);
}

/* ── Nav ── */
.rb-nav {
  position: sticky; top: 0; z-index: 100;
  background: rgba(8,8,8,0.95);
  border-bottom: 2px solid var(--border2);
  padding: 0 24px;
  image-rendering: pixelated;
}

.rb-nav::after {
  content: '';
  display: block;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--yellow), transparent);
  opacity: 0.3;
  margin-top: 0;
}

.rb-nav-inner {
  max-width: 800px; margin: 0 auto;
  display: flex; align-items: center; gap: 16px;
  height: 58px;
}

.rb-logo {
  display: flex; align-items: center; gap: 10px;
  text-decoration: none; flex-shrink: 0;
}

.rb-logo-box {
  width: 36px; height: 36px;
  border: 2px solid var(--border2);
  background: var(--elevated);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 2px;
  padding: 4px;
}

.rb-logo-pixel {
  background: var(--yellow);
  border-radius: 1px;
}

.rb-logo-text {
  font-family: var(--px);
  font-size: 8px;
  color: var(--white);
  line-height: 1.4;
}

.rb-logo-text span {
  color: var(--yellow);
}

.rb-nav-tabs {
  display: flex; gap: 4px; margin-left: auto;
}

.rb-tab {
  padding: 5px 12px;
  font-family: var(--px); font-size: 6px;
  cursor: pointer; transition: all 0.1s;
  border: 2px solid var(--border);
  background: transparent;
  color: var(--gray);
  letter-spacing: 0.5px;
  image-rendering: pixelated;
}

.rb-tab:hover {
  color: var(--white);
  border-color: var(--border2);
}

.rb-tab.active {
  background: var(--yellow);
  color: #2A1800;
  border-color: var(--yellow2);
}

/* ── Layout ── */
.rb-page { position: relative; z-index: 1; }

.rb-wrap {
  max-width: 800px; margin: 0 auto;
  padding: 0 24px 100px;
}

/* ── Hero ── */
.rb-hero {
  padding: 56px 0 44px;
}

.rb-hero-badge {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 14px;
  border: 2px solid var(--border2);
  background: var(--elevated);
  font-family: var(--px);
  font-size: 6px;
  color: var(--yellow);
  letter-spacing: 1.5px;
  margin-bottom: 28px;
}

.rb-hero-badge::before {
  content: '■';
  color: var(--yellow);
  animation: blink 1s step-end infinite;
}

.rb-hero h1 {
  font-family: var(--px);
  font-size: clamp(14px, 3vw, 20px);
  color: var(--yellow);
  letter-spacing: 3px;
  line-height: 1.6;
  text-shadow: 0 0 20px rgba(255,222,0,0.3),
               4px 4px 0 rgba(0,0,0,0.8);
  margin-bottom: 10px;
}

.rb-hero h1 span {
  color: var(--white);
}

.rb-hero-sub {
  font-family: var(--vt);
  color: var(--gray);
  font-size: 20px;
  max-width: 540px;
  line-height: 1.6;
  margin-top: 12px;
}

.rb-hero-meta {
  display: flex; flex-wrap: wrap; gap: 8px 20px;
  margin-top: 24px;
}

.rb-chip {
  display: flex; align-items: center; gap: 6px;
  font-family: var(--px); font-size: 6px;
  color: var(--gray); letter-spacing: 0.5px;
  border: 1px solid var(--border);
  padding: 4px 10px;
  background: var(--elevated);
}

.rb-chip::before { content: '▶'; font-size: 5px; color: var(--yellow); }

/* ── LCD Divider ── */
.rb-lcd-bar {
  height: 12px;
  background: var(--lcd);
  border: 2px solid #527A3E;
  border-radius: 2px;
  margin: 32px 0;
  position: relative;
  overflow: hidden;
}

.rb-lcd-bar::after {
  content: '';
  position: absolute; inset: 0;
  background: repeating-linear-gradient(
    90deg,
    transparent,
    transparent 10px,
    rgba(26,46,10,0.3) 10px,
    rgba(26,46,10,0.3) 11px
  );
}

/* ── Section cards ── */
.rb-section {
  background: var(--surface);
  border: 2px solid var(--border);
  padding: 24px 26px;
  margin-bottom: 8px;
  position: relative;
  animation: fadeIn 0.3s ease both;
}

/* Pixel corner decoration */
.rb-section::before,
.rb-section::after {
  content: '■';
  position: absolute;
  font-size: 6px;
  color: var(--border2);
  top: 6px;
}
.rb-section::before { left: 6px; }
.rb-section::after  { right: 6px; }

.rb-section:hover {
  border-color: rgba(255,222,0,0.25);
}

.rb-section-head {
  display: flex; align-items: center; gap: 14px;
  margin-bottom: 18px;
  padding-bottom: 14px;
  border-bottom: 1px dashed var(--border2);
}

.rb-section-icon {
  width: 44px; height: 44px; min-width: 44px;
  border: 2px solid var(--border2);
  background: var(--elevated);
  display: flex; align-items: center; justify-content: center;
  font-size: 20px;
}

.rb-section-num {
  font-family: var(--px);
  font-size: 6px;
  color: var(--gray);
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.rb-section-title {
  font-family: var(--px);
  font-size: 8px;
  color: var(--yellow);
  letter-spacing: 1px;
}

/* ── Content styles ── */
.rb-body {
  font-family: var(--vt);
  color: var(--gray); font-size: 18px; line-height: 1.75;
}

.rb-body + .rb-body { margin-top: 10px; }

.rb-body strong { color: var(--white); }

.rb-body a {
  color: var(--yellow);
  text-decoration: none;
  border-bottom: 1px dashed rgba(255,222,0,0.4);
}

.rb-body a:hover { border-color: var(--yellow); }

.rb-list {
  list-style: none; padding: 0; margin-top: 12px;
}

.rb-list li {
  font-family: var(--vt);
  color: var(--gray); font-size: 18px; line-height: 1.7;
  padding: 4px 0 4px 22px; position: relative;
}

.rb-list li::before {
  content: '▸';
  position: absolute; left: 0;
  color: var(--yellow); opacity: 0.7;
  font-size: 14px;
}

.rb-list li strong { color: var(--white); }

/* ── Callout boxes ── */
.rb-callout {
  border: 2px solid var(--border2);
  padding: 12px 16px; margin-top: 14px;
  font-family: var(--vt); font-size: 17px; line-height: 1.6;
  display: flex; gap: 12px; align-items: flex-start;
  position: relative;
}

.rb-callout::before {
  content: '';
  position: absolute; top: 0; left: 0;
  width: 4px; height: 100%;
}

.rb-callout.green {
  background: rgba(76,175,80,0.06);
  border-color: rgba(76,175,80,0.25);
  color: #6FCF77;
}
.rb-callout.green::before { background: #4CAF50; }

.rb-callout.yellow {
  background: rgba(255,222,0,0.05);
  border-color: rgba(255,222,0,0.2);
  color: var(--yellow);
}
.rb-callout.yellow::before { background: var(--yellow); }

.rb-callout.red {
  background: rgba(255,68,68,0.06);
  border-color: rgba(255,68,68,0.2);
  color: #FF6B6B;
}
.rb-callout.red::before { background: var(--red); }

.rb-callout.gray {
  background: var(--elevated);
  border-color: var(--border2);
  color: var(--gray);
}

.rb-callout-icon { flex-shrink: 0; font-size: 16px; margin-top: 2px; }

/* ── Table ── */
.rb-table {
  width: 100%; border-collapse: collapse;
  margin-top: 14px;
  font-family: var(--vt); font-size: 16px;
  border: 2px solid var(--border);
}

.rb-table th {
  text-align: left;
  font-family: var(--px); font-size: 5px;
  letter-spacing: 1px; text-transform: uppercase;
  color: var(--yellow);
  padding: 10px 12px;
  background: var(--elevated);
  border-bottom: 2px solid var(--border2);
}

.rb-table td {
  padding: 9px 12px; color: var(--gray);
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.rb-table td:first-child { color: var(--white); }
.rb-table tr:last-child td { border-bottom: none; }
.rb-table tr:hover td { background: rgba(255,255,255,0.02); }

/* ── Contact card ── */
.rb-contact {
  background: var(--elevated);
  border: 2px solid var(--border2);
  padding: 36px 32px;
  text-align: center;
  margin-top: 40px;
  position: relative;
  overflow: hidden;
}

.rb-contact::before {
  content: '▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓▓';
  position: absolute; top: 0; left: 0; right: 0;
  font-size: 8px; color: var(--yellow); opacity: 0.1;
  overflow: hidden; height: 16px;
  letter-spacing: -1px;
}

.rb-contact h3 {
  font-family: var(--px);
  font-size: 11px;
  color: var(--yellow);
  letter-spacing: 2px;
  margin-bottom: 12px;
  text-shadow: 0 0 15px rgba(255,222,0,0.2);
}

.rb-contact p {
  font-family: var(--vt);
  color: var(--gray); font-size: 19px;
  margin-bottom: 24px; line-height: 1.6;
}

.rb-contact-btn {
  display: inline-flex; align-items: center; gap: 8px;
  background: var(--yellow);
  color: #2A1800;
  font-family: var(--px); font-weight: bold; font-size: 7px;
  padding: 12px 24px;
  text-decoration: none;
  border: 3px solid var(--yellow2);
  letter-spacing: 1px;
  transition: all 0.1s;
  cursor: pointer; border-radius: 0;
}

.rb-contact-btn:hover {
  background: var(--yellow2);
  transform: translate(2px, 2px);
  box-shadow: none;
}

.rb-contact-btn:active {
  transform: translate(4px, 4px);
}

/* ── Stars display ── */
.rb-stars {
  display: flex; gap: 4px; justify-content: center;
  margin-bottom: 20px;
}

.rb-star {
  font-size: 14px;
  color: var(--yellow);
  animation: starPop 0.5s ease both;
}

/* ── Footer ── */
.rb-footer {
  text-align: center; padding-top: 48px;
  font-family: var(--vt); color: #3A3A3A; font-size: 16px;
  line-height: 2;
}

.rb-footer strong { color: var(--gray); }

.rb-footer-nav {
  display: flex; justify-content: center; gap: 0;
  margin-top: 12px;
}

.rb-footer-btn {
  background: none; border: 1px solid var(--border);
  color: var(--yellow); cursor: pointer;
  font-family: var(--px); font-size: 5px;
  padding: 6px 14px; letter-spacing: 1px;
  transition: background 0.1s;
}

.rb-footer-btn:hover { background: var(--elevated); }
.rb-footer-btn:first-child { border-right: none; }

/* ── Pixel marquee ── */
.rb-marquee-wrap {
  overflow: hidden;
  border-top: 1px solid var(--border);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  padding: 8px 0;
  margin: 36px 0;
}

.rb-marquee {
  display: flex; gap: 48px;
  animation: marquee 20s linear infinite;
  white-space: nowrap;
  font-family: var(--px); font-size: 6px;
  color: var(--border2); letter-spacing: 2px;
}

.rb-marquee span { color: var(--yellow); opacity: 0.4; }

/* ── Animations ── */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

@keyframes marquee {
  from { transform: translateX(0); }
  to   { transform: translateX(-50%); }
}

@keyframes starPop {
  from { transform: scale(0); opacity: 0; }
  to   { transform: scale(1); opacity: 1; }
}

/* ── Mobile ── */
@media (max-width: 600px) {
  .rb-wrap { padding: 0 14px 80px; }
  .rb-hero { padding: 36px 0 32px; }
  .rb-section { padding: 18px 16px; }
  .rb-nav-tabs .rb-tab { padding: 4px 8px; font-size: 5px; }
  .rb-contact { padding: 24px 16px; }
  .rb-hero h1 { font-size: 12px; }
}
`;

// ── Logo pixels (simple Game Boy brick pattern)
function LogoPixels() {
  const pattern = [1,0,1,1, 1,1,1,0, 0,1,1,1, 1,1,0,1];
  return (
    <div className="rb-logo-box">
      {pattern.map((on, i) => (
        <div key={i} className="rb-logo-pixel" style={{ opacity: on ? 1 : 0.1 }} />
      ))}
    </div>
  );
}

// ── Marquee text
function Marquee() {
  const items = ["RETROBRIC", "★", "PRIVACY POLICY", "★", "LEGAL DOCS", "★", "SUPER MOUSE STUDIO", "★",
                 "RETROBRIC", "★", "PRIVACY POLICY", "★", "LEGAL DOCS", "★", "SUPER MOUSE STUDIO", "★"];
  return (
    <div className="rb-marquee-wrap">
      <div className="rb-marquee">
        {items.map((t, i) => (
          <span key={i} className={t === "★" ? "" : ""}>{t}</span>
        ))}
        {items.map((t, i) => (
          <span key={`b${i}`}>{t}</span>
        ))}
      </div>
    </div>
  );
}

// ── Privacy Policy sections
const PP_SECTIONS = [
  {
    num: "01", emoji: "📋",
    title: "OVERVIEW",
    content: () => (
      <>
        <p className="rb-body">
          <strong>{DEV_NAME}</strong> ("developer", "we", "I") built <strong>{APP_NAME}</strong> as a free
          retro game app available on Google Play. This Privacy Policy explains how we handle
          information related to your use of the app.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          By downloading or using {APP_NAME}, you agree to the terms described here.
          If you disagree, please discontinue use of the app.
        </p>
        <div className="rb-callout green">
          <span className="rb-callout-icon">✓</span>
          <span>
            <strong>Simple summary:</strong> RetroBric does not collect, store, or sell your personal data.
            No account is required to play. All game progress is stored locally on your device only.
          </span>
        </div>
      </>
    )
  },
  {
    num: "02", emoji: "📊",
    title: "INFORMATION WE COLLECT",
    content: () => (
      <>
        <p className="rb-body">
          <strong>RetroBric is designed to be privacy-first.</strong> We collect the bare minimum needed
          to provide and improve the game:
        </p>
        <table className="rb-table">
          <thead>
            <tr>
              <th>Data Type</th>
              <th>Collected?</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Name / Email</td><td>❌ No</td><td>No account required</td></tr>
            <tr><td>Location</td><td>❌ No</td><td>Not requested</td></tr>
            <tr><td>Game progress</td><td>Local only</td><td>Stored on device via SharedPreferences</td></tr>
            <tr><td>High scores</td><td>Local only</td><td>Stored on device, never uploaded</td></tr>
            <tr><td>Stars earned</td><td>Local only</td><td>In-game currency, device only</td></tr>
            <tr><td>Device info</td><td>Anonymous</td><td>Crash reporting to fix bugs</td></tr>
            <tr><td>Payment data</td><td>❌ No</td><td>App is completely free</td></tr>
          </tbody>
        </table>
        <div className="rb-callout yellow">
          <span className="rb-callout-icon">ℹ</span>
          <span>
            Your high scores, stars, theme purchases, and level progress are stored only on your device.
            Uninstalling the app removes all this data permanently.
          </span>
        </div>
      </>
    )
  },
  {
    num: "03", emoji: "🔧",
    title: "THIRD-PARTY SERVICES",
    content: () => (
      <>
        <p className="rb-body">
          RetroBric may use the following third-party services that have their own privacy policies.
          We recommend reviewing each:
        </p>
        <ul className="rb-list">
          <li>
            <strong>Google Play Services</strong> — for app distribution, updates, and crash reporting.{" "}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy ↗</a>
          </li>
          <li>
            <strong>Firebase Crashlytics (optional)</strong> — anonymous crash reports to help fix bugs.
            No personally identifiable information is included.
          </li>
          <li>
            <strong>Google AdMob (if ads enabled)</strong> — advertising platform may collect anonymised
            device identifiers to serve contextual ads.{" "}
            <a href="https://support.google.com/admob/answer/6128543" target="_blank" rel="noreferrer">AdMob Policy ↗</a>
          </li>
        </ul>
        <div className="rb-callout gray">
          <span className="rb-callout-icon">⚠</span>
          <span>
            If the app displays ads, Google AdMob may use your advertising ID per Google's privacy policy.
            You can opt out of personalised ads in your device settings under <strong>Google → Ads</strong>.
          </span>
        </div>
      </>
    )
  },
  {
    num: "04", emoji: "🔒",
    title: "DATA SECURITY",
    content: () => (
      <>
        <p className="rb-body">
          All game data (progress, stars, themes, high scores, settings) is stored locally on
          your device using Android's SharedPreferences system. This data is:
        </p>
        <ul className="rb-list">
          <li>Never transmitted to our servers</li>
          <li>Never shared with third parties by us</li>
          <li>Protected by your device's own security model</li>
          <li>Automatically deleted when you uninstall the app</li>
        </ul>
        <p className="rb-body" style={{ marginTop: 12 }}>
          While we strive to implement secure practices, no method of data storage is 100% secure.
          We cannot guarantee absolute protection against all risks.
        </p>
      </>
    )
  },
  {
    num: "05", emoji: "👶",
    title: "CHILDREN'S PRIVACY",
    content: () => (
      <>
        <p className="rb-body">
          RetroBric is a family-friendly retro game suitable for all ages. It is rated{" "}
          <strong>Everyone</strong> on Google Play and does not knowingly collect personal
          information from children under 13.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          The app requires no registration, login, or personal information to play.
          If you are a parent and believe your child has provided personal data to us,
          please contact us at <a href={`mailto:${DEV_EMAIL}`}>{DEV_EMAIL}</a>.
        </p>
        <div className="rb-callout green">
          <span className="rb-callout-icon">✓</span>
          <span>No account required. No personal data collected. Safe for all ages.</span>
        </div>
      </>
    )
  },
  {
    num: "06", emoji: "🔄",
    title: "CHANGES TO THIS POLICY",
    content: () => (
      <>
        <p className="rb-body">
          We may update this Privacy Policy to reflect changes in the app or applicable laws.
          Updates will be posted at this URL with a revised effective date.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          Continued use of the app after any changes constitutes your acceptance of the updated policy.
          We encourage you to review this page periodically.
        </p>
        <div className="rb-callout yellow">
          <span className="rb-callout-icon">📅</span>
          <span>Current effective date: <strong>{EFFECTIVE_DATE}</strong></span>
        </div>
      </>
    )
  },
];

// ── Terms & Conditions sections
const TC_SECTIONS = [
  {
    num: "01", emoji: "📜",
    title: "ACCEPTANCE OF TERMS",
    content: () => (
      <>
        <p className="rb-body">
          By downloading, installing, or using <strong>{APP_NAME}</strong> (the "App"), you agree to
          be bound by these Terms and Conditions. If you do not agree, do not use the App.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          These terms apply to all users of the App. We reserve the right to modify these terms
          at any time. Continued use constitutes acceptance.
        </p>
      </>
    )
  },
  {
    num: "02", emoji: "📱",
    title: "LICENSE TO USE",
    content: () => (
      <>
        <p className="rb-body">
          <strong>{DEV_NAME}</strong> grants you a limited, non-exclusive, non-transferable, revocable
          license to use the App for personal, non-commercial purposes on devices you own or control.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>You may <strong>not</strong>:</p>
        <ul className="rb-list">
          <li>Copy, modify, or distribute the App or its content</li>
          <li>Reverse engineer, decompile, or disassemble the App</li>
          <li>Remove or alter any proprietary notices or labels</li>
          <li>Use the App for any commercial purpose without written permission</li>
          <li>Transfer your license or "sell" access to the App</li>
          <li>Use automated tools, bots, or scripts to interact with the App</li>
        </ul>
      </>
    )
  },
  {
    num: "03", emoji: "🎮",
    title: "GAME CONTENT & VIRTUAL ITEMS",
    content: () => (
      <>
        <p className="rb-body">
          All game content — including levels, graphics, sounds, animations, themes, and the star
          economy — is owned by {DEV_NAME} and protected by applicable intellectual property laws.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          <strong>Stars</strong> (in-game currency) are virtual items with no real-world monetary value.
          They cannot be transferred, sold, or exchanged outside the App. Stars may be reset if you
          reinstall the App, as progress is stored locally on your device.
        </p>
        <div className="rb-callout yellow">
          <span className="rb-callout-icon">ℹ</span>
          <span>
            RetroBric is currently free with no in-app purchases. Stars are earned through gameplay only.
            Shop items (skins, LCD themes, button colors) are purchased with stars earned in-game.
            No real money is required or accepted within the App.
          </span>
        </div>
      </>
    )
  },
  {
    num: "04", emoji: "🌐",
    title: "INTERNET & OFFLINE USE",
    content: () => (
      <>
        <p className="rb-body">
          RetroBric is designed for <strong>offline play</strong>. An internet connection is only required to:
        </p>
        <ul className="rb-list">
          <li>Download the App initially from Google Play</li>
          <li>Receive app updates from the Play Store</li>
          <li>Load advertisements (if ads are enabled in the App)</li>
        </ul>
        <p className="rb-body" style={{ marginTop: 10 }}>
          All gameplay —  Snake, and future games — functions fully without an internet connection.
        </p>
      </>
    )
  },
  {
    num: "05", emoji: "🚫",
    title: "PROHIBITED CONDUCT",
    content: () => (
      <>
        <p className="rb-body">When using the App, you agree not to:</p>
        <ul className="rb-list">
          <li>Cheat, exploit bugs, or use third-party tools to manipulate game state or high scores</li>
          <li>Attempt to hack, overload, or interfere with the App's functionality</li>
          <li>Use the App in violation of applicable local, national, or international laws</li>
          <li>Impersonate any person or misrepresent your affiliation with any entity</li>
          <li>Collect or harvest any data from the App</li>
        </ul>
        <div className="rb-callout red">
          <span className="rb-callout-icon">✗</span>
          <span>
            Violation of these terms may result in termination of your access to the App without notice.
          </span>
        </div>
      </>
    )
  },
  {
    num: "06", emoji: "📢",
    title: "ADVERTISEMENTS",
    content: () => (
      <>
        <p className="rb-body">
          The App may display advertisements provided by Google AdMob. By using the App,
          you consent to the display of such advertisements.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          We are not responsible for the content of third-party advertisements. {DEV_NAME} does not
          endorse any products or services advertised within the App. Advertisement content is
          governed by Google's advertising policies.
        </p>
      </>
    )
  },
  {
    num: "07", emoji: "⚖️",
    title: "DISCLAIMER OF WARRANTIES",
    content: () => (
      <>
        <p className="rb-body">
          The App is provided on an <strong>"AS IS" and "AS AVAILABLE"</strong> basis without any
          warranty of any kind, express or implied, including but not limited to:
        </p>
        <ul className="rb-list">
          <li>Merchantability or fitness for a particular purpose</li>
          <li>Uninterrupted or error-free operation</li>
          <li>Accuracy or completeness of any content</li>
          <li>Freedom from bugs or other harmful components</li>
        </ul>
        <p className="rb-body" style={{ marginTop: 10 }}>
          Your use of the App is entirely at your own risk.
        </p>
      </>
    )
  },
  {
    num: "08", emoji: "🛡️",
    title: "LIMITATION OF LIABILITY",
    content: () => (
      <>
        <p className="rb-body">
          To the maximum extent permitted by law, <strong>{DEV_NAME}</strong> shall not be liable
          for any indirect, incidental, special, or consequential damages, including:
        </p>
        <ul className="rb-list">
          <li>Loss of game progress, high scores, or stars due to device failure or reinstallation</li>
          <li>Loss of purchased shop themes (skins, LCD colors, button colors)</li>
          <li>Interruption of service or App unavailability</li>
          <li>Any other loss or damage arising from your use of the App</li>
        </ul>
        <p className="rb-body" style={{ marginTop: 10 }}>
          In no event shall our total liability exceed the amount you paid for the App
          (which is zero, as the App is free).
        </p>
      </>
    )
  },
  {
    num: "09", emoji: "🔏",
    title: "INTELLECTUAL PROPERTY",
    content: () => (
      <>
        <p className="rb-body">
          All content within {APP_NAME} — including graphics, UI design, sound effects, background music,
          animations, game logic, themes, and source code — is the exclusive intellectual property of{" "}
          <strong>{DEV_NAME}</strong> and is protected by copyright and other applicable laws.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          The name "RetroBric", logo, and associated branding are owned by {DEV_NAME}.
          Unauthorized use, reproduction, or distribution of any app content is strictly prohibited.
        </p>
      </>
    )
  },
  {
    num: "10", emoji: "📍",
    title: "GOVERNING LAW",
    content: () => (
      <>
        <p className="rb-body">
          These Terms shall be governed by and construed in accordance with the laws of India,
          without regard to conflict of law principles.
        </p>
        <p className="rb-body" style={{ marginTop: 10 }}>
          Any disputes arising from these Terms or your use of the App shall be subject to
          the exclusive jurisdiction of the courts located in India.
        </p>
      </>
    )
  },
  {
    num: "11", emoji: "📧",
    title: "CONTACT & SUPPORT",
    content: () => (
      <>
        <p className="rb-body">
          For questions about these Terms, bugs, feedback, or support requests, contact us:
        </p>
        <ul className="rb-list">
          <li><strong>Developer:</strong> {DEV_NAME}</li>
          <li><strong>Email:</strong> <a href={`mailto:${DEV_EMAIL}`}>{DEV_EMAIL}</a></li>
          <li><strong>App:</strong> {APP_NAME}</li>
          <li><strong>Package:</strong> {PACKAGE}</li>
        </ul>
        <div className="rb-callout green">
          <span className="rb-callout-icon">✓</span>
          <span>
            We typically respond to all enquiries within 48 hours.
            Your feedback helps us make RetroBric better for everyone!
          </span>
        </div>
      </>
    )
  },
];

// ── Main Component ───────────────────────────────────────────────
export default function RetroBricsPrivacyPolicy() {
  const [activeTab, setActiveTab] = useState("privacy");

  useEffect(() => {
    const el = document.createElement("style");
    el.textContent = CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeTab]);

  const isPrivacy = activeTab === "privacy";
  const sections = isPrivacy ? PP_SECTIONS : TC_SECTIONS;

  return (
    <div className="rb-page">
      <div className="rb-dots" />
      <div className="rb-glow" />

      {/* Nav */}
      <nav className="rb-nav">
        <div className="rb-nav-inner">
          <a href="#" className="rb-logo">
            <LogoPixels />
            <div className="rb-logo-text">
              <span>BRICK</span><br />GAME
            </div>
          </a>
          <div className="rb-nav-tabs">
            <button
              className={`rb-tab ${isPrivacy ? "active" : ""}`}
              onClick={() => setActiveTab("privacy")}
            >
              PRIVACY
            </button>
            <button
              className={`rb-tab ${!isPrivacy ? "active" : ""}`}
              onClick={() => setActiveTab("terms")}
            >
              TERMS
            </button>
          </div>
        </div>
      </nav>

      <div className="rb-wrap">
        {/* Hero */}
        <header className="rb-hero" key={activeTab}>
          <div className="rb-hero-badge">
            {isPrivacy ? "LEGAL · PRIVACY POLICY" : "LEGAL · TERMS & CONDITIONS"}
          </div>

          <h1>
            {isPrivacy
              ? <><span>PRIVACY </span>POLICY</>
              : <><span>TERMS & </span>CONDITIONS</>
            }
          </h1>

          <p className="rb-hero-sub">
            {isPrivacy
              ? `How ${DEV_NAME} handles information related to your use of ${APP_NAME}.`
              : `The rules and agreements that govern your use of ${APP_NAME} on Google Play.`
            }
          </p>

          <div className="rb-hero-meta">
            <span className="rb-chip">EFFECTIVE {EFFECTIVE_DATE}</span>
            <span className="rb-chip">BY {DEV_NAME}</span>
            <span className="rb-chip">
              {isPrivacy ? "NO PERSONAL DATA COLLECTED" : "FREE APP · NO IAP"}
            </span>
          </div>
        </header>

        {/* LCD bar divider */}
        <div className="rb-lcd-bar" />

        {/* Pixel marquee */}
        <Marquee />

        {/* Sections */}
        {sections.map((s, i) => (
          <div
            key={`${activeTab}-${s.num}`}
            className="rb-section"
            style={{ animationDelay: `${i * 0.04}s` }}
          >
            <div className="rb-section-head">
              <div className="rb-section-icon">{s.emoji}</div>
              <div>
                <div className="rb-section-num">SECTION {s.num}</div>
                <div className="rb-section-title">{s.title}</div>
              </div>
            </div>
            <s.content />
          </div>
        ))}

        {/* Contact */}
        <div className="rb-contact">
          <div className="rb-stars">
            {[0,1,2,3,4].map(i => (
              <span key={i} className="rb-star" style={{ animationDelay: `${i * 0.1}s` }}>★</span>
            ))}
          </div>
          <h3>INSERT COIN TO CONTACT</h3>
          <p>
            Got questions about {APP_NAME} or these legal docs?<br />
            We'll get back to you within 48 hours.
          </p>
          <a href={`mailto:${DEV_EMAIL}`} className="rb-contact-btn">
            ▶ &nbsp;{DEV_EMAIL}
          </a>
        </div>

        {/* Footer */}
        <footer className="rb-footer">
          <div className="rb-lcd-bar" style={{ margin: "40px 0 28px", opacity: 0.4 }} />
          <p>© 2026 <strong>{DEV_NAME}</strong> · {APP_NAME}</p>
          <p style={{ marginTop: 4 }}>
            E-9999 · SUPER MOUSE · NOT AFFILIATED WITH GOOGLE LLC
          </p>
          <div className="rb-footer-nav" style={{ marginTop: 14 }}>
            <button className="rb-footer-btn" onClick={() => setActiveTab("privacy")}>
              PRIVACY POLICY
            </button>
            <button className="rb-footer-btn" onClick={() => setActiveTab("terms")}>
              TERMS & CONDITIONS
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}